import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getNotifications, getUnreadNotificationCount,
  markNotificationRead, markAllNotificationsRead
} from '../api/api';
import { Avatar } from './Charts';

const POLL_MS = 30000;

function timeAgo(dateStr) {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'ahora mismo';
  if (mins < 60) return `hace ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return `hace ${days} d`;
}

function NotificationBell({ isAdmin }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const containerRef = useRef(null);

  const refreshCount = () => {
    getUnreadNotificationCount().then(res => setUnreadCount(res.data.count)).catch(() => {});
  };

  useEffect(() => {
    refreshCount();
    const interval = setInterval(refreshCount, POLL_MS);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    // Se vuelve a pedir cada vez que se abre — así nunca se queda viendo una lista vieja
    // mientras el contador de arriba ya avanzó por el polling.
    if (next) {
      getNotifications().then(res => setNotifications(res.data)).catch(() => {});
    }
  };

  const handleItemClick = async (n) => {
    if (!n.read) {
      await markNotificationRead(n.id).catch(() => {});
      setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    setOpen(false);
    // Se manda el id de la referencia por state — el Dashboard/Admin lo detectan y abren
    // directo la ventana de detalle de esa referencia, aunque ya estés en esa página.
    navigate(isAdmin ? '/admin' : '/dashboard', { state: { openReferralId: n.referral_id, navKey: Date.now() } });
  };

  const handleMarkAll = async (e) => {
    e.stopPropagation();
    await markAllNotificationsRead().catch(() => {});
    setNotifications(prev => prev.map(x => ({ ...x, read: true })));
    setUnreadCount(0);
  };

  return (
    <div className="notif-bell-container" ref={containerRef}>
      <button className="notif-bell" onClick={toggleOpen} aria-label="Notificaciones">
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && <span className="notif-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>}
      </button>

      {open && (
        <div className="notif-dropdown">
          <div className="notif-dropdown-header">
            <span>Notificaciones</span>
            {unreadCount > 0 && (
              <button className="notif-mark-all" onClick={handleMarkAll}>Marcar todas como leídas</button>
            )}
          </div>
          <div className="notif-list">
            {notifications.length === 0 && (
              <div className="notif-empty">Sin notificaciones todavía</div>
            )}
            {notifications.map((n) => (
              <div
                key={n.id}
                className={`notif-item${n.read ? '' : ' notif-item-unread'}`}
                onClick={() => handleItemClick(n)}
              >
                <Avatar src={n.actor_avatar_url} seed={n.actor_name || 'hub-networking'} size={32} />
                <div className="notif-item-body">
                  <div className="notif-item-message">{n.message}</div>
                  <div className="notif-item-time">{timeAgo(n.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
